import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MetaTagsService } from '../../shared/services/';

import { LegacyConfig, LegacyConfigService } from '@scalefast/config-angular';
import { CATEGORY_FILTERS_OPTIONS, CategoryPageFilters, Filter, Product } from '@scalefast/core';
import { ProductsDisplayedAction } from '@scalefast/ecommerce-core';

import { GetProductsByFilterUseCaseAdapter, SearchResultsPageQueryParamsAdapter } from '@scalefast/sam';
import { AppProductListPageComponent } from '../../core/classes/product-list-page.component';
import { AppStoreProductListComponent } from '../product/product-list/product-list.component';

import { Location } from '@angular/common';
import { filter } from 'rxjs';
import { AnalyticsStoreService } from 'src/app/shared/services';
import { AppSearchFilterComponent } from '../../shared/components/filter/search-filter.component';
import { AppPaginationComponent } from '../../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-search-results-page',
  styleUrls: ['./search-results-page.component.scss'],
  templateUrl: './search-results-page.component.html',
  standalone: true,
  imports: [AppSearchFilterComponent, AppStoreProductListComponent, AppPaginationComponent],
})
export class AppSearchResultsPageComponent extends AppProductListPageComponent implements OnInit, OnDestroy {
  @ViewChild(AppStoreProductListComponent) override storeProductListComponent: AppStoreProductListComponent | undefined;

  public queryString: string;
  public searchFilter: Filter = { name: 'search', values: [], groupBy: '' };
  public isActiveSearch: boolean = false;
  public searchResults: Array<Product> = [];
  public filters: CategoryPageFilters = {
    offset: CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_OFFSET,
    pageSize: CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_SIZE,
    filters: [],
  };
  colorReferences: Record<string, string> | undefined = {};
  public currentTemplate: string = 'search';
  private legacyConfig: LegacyConfig;

  constructor(
    private legacyConfigService: LegacyConfigService,
    private analyticsStoreService: AnalyticsStoreService,
    private metaTags: MetaTagsService,
    private location: Location,
  ) {
    super();
    this.legacyConfig = this.legacyConfigService.get();
    this.queryString = this.route.snapshot.queryParamMap.get('query') ?? '';
    this.searchFilter = { name: 'search', values: [this.queryString], groupBy: '' };
  }

  ngOnInit() {
    this.metaTags.setMetaTags({
      page: 'search',
      robots: 'noindex,follow',
    });

    if (!this.queryString) {
      this.resetSearchFilters();
      return;
    }

    this.filtersSubscription = this.filterService.filtersSubject
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters: CategoryPageFilters) => {
        this.currentOffset = filters.offset;
        this.filters = filters;

        this.getResults();
        this.setQueryParams(this.filters);
      });

    this.setFiltersFromQueryString();

    this.colorReferences = this.legacyConfig.metacolorReferences;

    this.wishlistService
      .getWishlist$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((wishlist) => {
        this.wishlist = wishlist;
      });

    this.analyticsStoreService.pg_content(this.currentTemplate);
  }

  setSearchFilters(): void {
    const hasSearchFilter = this.hasFilterByName('search');
    const hasGroupByCritFilter = this.hasFilterByGroup('crit');

    if (hasSearchFilter) {
      if (hasGroupByCritFilter) {
        return;
      }

      const critFilter = this.createFilter('()', [], 'crit');
      this.filters.filters.push(critFilter);
      return;
    }

    if (hasGroupByCritFilter) {
      this.filters.filters.push(this.searchFilter);
      return;
    }

    const critFilter = this.createFilter('()', [], 'crit');
    this.filters.filters.push(critFilter, this.searchFilter);
  }

  removeDuplicateFilters(): void {
    const critFilterCount = this.getFilterCountByGroup('crit');

    if (critFilterCount > 1) {
      this.filters.filters = this.filters.filters.filter((filter: Filter) => filter.name !== '()');
    }
  }

  setQueryParams(filters: CategoryPageFilters) {
    const currentUrl = this.location.path().split('?')[0];
    const offsetPageSizeString = `offset=${filters.offset}&pageSize=${filters.pageSize}&`;
    let params = `${SearchResultsPageQueryParamsAdapter.transformFilterToQueryString(filters)}`;
    params = params.replace(offsetPageSizeString, '');
    const newURL = `${currentUrl}?${params}`;
    this.location.replaceState(newURL, '');
  }

  setFiltersFromQueryString(): void {
    if (this.route.snapshot.queryParamMap.keys.length < 1) {
      return;
    }

    const filtersFromUrl = SearchResultsPageQueryParamsAdapter.buildFiltersFromQueryParams(this.route.snapshot.queryParamMap);

    this.filterService.updateFiltersConfiguration(filtersFromUrl);
    this.currentOffset = filtersFromUrl.offset;

    this.filterService.setFilters(filtersFromUrl);
  }

  getFilteredProducts(): void {
    const filterParams = {
      ...this.filterService.getStoreSearchParams(),
      ...{ count: this.filters.pageSize, offset: this.filters.offset },
      ...GetProductsByFilterUseCaseAdapter.buildParamsFromSearchResults(this.filters),
      search_type: 'strict',
    };

    let action: ProductsDisplayedAction = filterParams.offset === 0 ? 'search-bar-result' : 'search-bar-result-more';

    const excludedPohFeedIDs = ['71611', '71638', '71663', '71664'];
    this.filterService
      .getProductsByFilters(filterParams, action)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data: any) => {
          data.products = data.products.filter((product: any) => {
            return product.v4ApiResponse.feeds.every((feed: any) => !excludedPohFeedIDs.includes(feed.feedID));
          });
          return data.products.length > 0;
        }),
      )
      .subscribe((data: any) => {
        if (this.isLoadMore) {
          this.searchResults = this.searchResults.concat(data.products);
          this.isLoadMore = false;
        } else {
          this.searchResults = data.products;
        }
        this.isActiveSearch = true;
        this.totalProducts = data.total;
        this.hasNextPage(data.total);
        this.hasLoadMore(data.total);
      });
  }

  getResults(): void {
    this.setSearchFilters();
    this.removeDuplicateFilters();
    this.getFilteredProducts();
  }

  ngOnDestroy(): void {
    this.filters.filters = [];
    this.filterService.resetFilters();
    this.filtersSubscription?.unsubscribe();
  }

  private hasFilterByName(name: string): boolean {
    return this.filters.filters.some((filter: Filter) => filter.name === name);
  }

  private hasFilterByGroup(group: string): boolean {
    return this.filters.filters.some((filter: Filter) => filter.groupBy === group);
  }

  private createFilter(name: string, values: Array<string>, groupBy: string): Filter {
    return { name, values, groupBy };
  }

  private getFilterCountByGroup(group: string): number {
    return this.filters.filters.filter((filter: Filter) => filter.groupBy === group).length;
  }

  private resetSearchFilters(): void {
    this.searchFilter = { name: 'search', values: [], groupBy: '' };
    this.isActiveSearch = false;
    this.searchResults = [];
  }
}
